import { generateQuickGuid } from "./helper";
import {
  Subject,
  PersonSubject,
  BusinessSubject,
  CaseData,
  SocialMedia,
} from "../../../types/CaseTypes";

/**
 * Factory function to create a new subject with default values
 * @param isPrimary - Whether this is the primary subject
 * @param relatedSubjectId - ID of the subject this one relates to (for non-primary subjects)
 * @returns New subject object with default values
 */
export const createNewSubject = (
  isPrimary: boolean = false,
  relatedSubjectId: string = ""
): PersonSubject => {
  const defaultSocialMedia: SocialMedia[] = [
    { type: "facebook", value: "" },
    { type: "twitter", value: "" },
    { type: "instagram", value: "" },
    { type: "linkedin", value: "" },
    { type: "personal", value: "" },
  ];

  return {
    subject_id: generateQuickGuid("subject"),
    subject_type: "person",
    first_name: "",
    middle_name: "",
    last_name: "",
    is_primary: isPrimary,
    gender: "",
    marital_status: "",
    street_address: "",
    city: "",
    state: "",
    relationship_to_subject: "",
    ssn: "",
    dob: "",
    zip_code: "",
    phone: [""],
    email: [""],
    deceased: false,
    // Nothing to relate to: no subjects yet, or a business primary.
    relationship:
      !isPrimary && !relatedSubjectId
        ? []
        : [
            {
              relationshipStatus: "",
              subjectId: relatedSubjectId,
              subjectIndex: isPrimary ? -1 : 0,
            },
          ],
    date_of_death: "",
    death_cert_order_date: "",
    death_cert_received_date: "",
    place_of_death: "",
    place_of_birth: "",
    father_name: "",
    mother_name: "",
    social_media: defaultSocialMedia,
  };
};

export const createBusinessSubject = (
  from: Subject = createNewSubject()
): BusinessSubject => ({
  subject_id: from.subject_id,
  is_primary: from.is_primary,
  street_address: from.street_address,
  city: from.city,
  state: from.state,
  zip_code: from.zip_code,
  phone: from.phone,
  email: from.email,
  social_media: from.social_media,
  subject_type: "business",
  business_name: "",
});

// The form keeps both types' values while editing; this strips what each type doesn't save.
export const toSavedSubjects = (subjects: Subject[]): Subject[] => {
  const businessIds = new Set(
    subjects
      .filter((subject) => subject.subject_type === "business")
      .map((subject) => subject.subject_id)
  );
  return subjects.map((subject): Subject => {
    if (subject.subject_type === "business") {
      return {
        ...createBusinessSubject(subject),
        business_name: subject.business_name,
      };
    }
    const { business_name, ...person } = subject as PersonSubject & {
      business_name?: string;
    };
    return {
      ...person,
      relationship: person.relationship.filter(
        (relation) => !businessIds.has(relation.subjectId)
      ),
    };
  });
};

export const relatedSubjectIdFor = (subjects: Subject[]): string =>
  subjects[0]?.subject_type === "business" ? "" : subjects[0]?.subject_id ?? "";

/**
 * Factory function to create initial case values
 * @returns New case object with default values
 */
export const newCaseInitValues = (): CaseData => {
  return {
    status: "active",
    primary_subject: "",
    flag: "",
    client_id: "",
    user_id: "",
    case_details: {
      client_information: {
        client_file_no: "",
        client_loan: "",
        phone_number: "",
        date_of_sale: "",
        reopened_date: "",
      },
      budget_information: {
        scheduled: 0,
        used: 0,
      },
      case_information: {
        file_no: "",
        date_referred: "",
        follow_up: "",
        due_date: "",
        case_type: "",
        subject_first_name: "",
        subject_last_name: "",
        subject_property_address: {
          street_address: "",
          city: "",
          state: "",
          zip_code: "",
        },
        sale_date: "",
        city: "",
        death_certificate_ordered: "",
        death_certificate_received: "",
        referral_notes: "",
        case_info: "",
      },
      field_resource_information: [
        {
          id: "",
          date_hired: "",
          due_date: "",
          hours_authorized: 0,
          hours_billed: 0,
          miles_billed: 0,
          database_billed: 0,
          report_received_date: "",
          invoice_received_date: "",
          invoice_approved_date: "",
          rating: 0,
          type: "investigator",
        },
      ],
      assigment_information: {
        investigator_name: "",
        event: "Case Created",
        supervisor_name: "",
      },
      Subjects_information: [
        createNewSubject(true, ""), // Primary subject
      ],
    },
  };
};
